import { validateEmail } from '../../utils/validation';

/**
 * Client-side mirror of the backend `AdditionalEmailValidation`: each non-blank entry must be a valid
 * address, unique within the list, and must not duplicate the primary — all case-insensitive. Blank
 * rows are ignored (they are dropped by {@link cleanAdditionalEmails} before submit).
 *
 * @returns the first validation message, or `undefined` when the list is valid.
 */
export function validateAdditionalEmails(
  additionalEmails: string[],
  primaryEmail: string,
): string | undefined {
  const primaryNorm = primaryEmail.trim().toLowerCase();
  const seen = new Set<string>();

  for (const raw of additionalEmails) {
    const value = raw.trim();
    if (!value) continue;
    if (!validateEmail(value)) {
      return `'${value}' is not a valid email address`;
    }
    const norm = value.toLowerCase();
    if (primaryNorm && norm === primaryNorm) {
      return `'${value}' duplicates the primary email address`;
    }
    if (seen.has(norm)) {
      return `'${value}' is listed more than once`;
    }
    seen.add(norm);
  }

  return undefined;
}

/** Drops blank rows and trims the rest, ready for submission. */
export function cleanAdditionalEmails(additionalEmails: string[]): string[] {
  return additionalEmails.map((e) => e.trim()).filter((e) => e.length > 0);
}

/**
 * Splits a pasted blob into individual addresses. Handles the shapes people actually paste out of a
 * mail client or spreadsheet: one-per-line, comma-separated, semicolon-separated, or a mix.
 *
 * Only tokens containing `@` are kept, so `Name <a@b.com>` yields just the address rather than a
 * stray "Name" row. Surrounding angle brackets and quotes are stripped.
 *
 * @returns the addresses found, in paste order. Fewer than two means the caller should let the
 *          browser handle the paste normally (a plain single-address paste into one field).
 */
export function parsePastedEmails(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((token) => token.trim().replace(/^[<"']+/, '').replace(/[>"',.]+$/, ''))
    .filter((token) => token.includes('@'));
}

/**
 * Merges freshly pasted addresses into an existing row list, replacing the row that was pasted into
 * and appending the remainder after it. Blank rows are preserved; addresses already present anywhere
 * in the list are skipped (case-insensitive) so a re-paste cannot duplicate rows.
 *
 * Duplicates of the *primary* address are deliberately NOT filtered here — they are inserted so
 * validation can tell the user why the address was rejected, rather than silently swallowing it.
 */
export function mergePastedEmails(
  current: string[],
  targetIndex: number,
  pasted: string[],
): string[] {
  const existing = new Set(
    current
      .filter((_, i) => i !== targetIndex)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0),
  );

  const fresh: string[] = [];
  for (const email of pasted) {
    const norm = email.toLowerCase();
    if (existing.has(norm)) continue;
    existing.add(norm);
    fresh.push(email);
  }

  if (fresh.length === 0) return current;

  return [...current.slice(0, targetIndex), ...fresh, ...current.slice(targetIndex + 1)];
}
